import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * The editor as the tenant's app (`features.tenantTools`): talk to what a
 * tenant runs now and see which versions those are.
 */
export type TalkNowResult = components["schemas"]["TalkNow"];
export type TenantVersions = components["schemas"]["TenantVersions"];
export type EnvironmentsReport = components["schemas"]["EnvironmentsReport"];
export type EnvironmentTenant = components["schemas"]["EnvironmentTenant"];
export type CompatStatus = components["schemas"]["EnvironmentCompat"]["status"];

export function useTalkNowMutation(): UseMutationResult<
  TalkNowResult,
  unknown,
  string
> {
  const client = useApiClient();
  return useMutation({
    async mutationFn(previewTenantId: string) {
      return unwrap(
        await client.POST("/v1/tenants/{previewTenantId}/talk-now", {
          params: { path: { previewTenantId } },
        }),
      );
    },
    retry: false,
  });
}

export function useTenantVersionsQuery(
  previewTenantId: string | undefined,
  enabled: boolean,
): UseQueryResult<TenantVersions> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.tenantVersions(previewTenantId ?? ""),
    enabled: enabled && previewTenantId !== undefined,
    async queryFn({ signal }) {
      return unwrap(
        await client.GET("/v1/tenants/{previewTenantId}/versions", {
          params: { path: { previewTenantId: previewTenantId ?? "" } },
          signal,
        }),
      );
    },
  });
}

export function useEnvironmentsQuery(): UseQueryResult<EnvironmentsReport> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.environments,
    async queryFn({ signal }) {
      return unwrap(await client.GET("/v1/environments", { signal }));
    },
  });
}
